/**
 * Validate NPI number format
 * @param {string} npi - NPI number to validate
 */
export const isValidNpi = (npi = '') => /^\d{10}$/.test((npi || '').trim());

/**
 * Common public email domains
 */
const PUBLIC_DOMAINS = new Set([
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'msn.com',
]);

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
export const isValidEmail = (email = '') =>
  /^[a-zA-Z0-9_.+-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(
    (email || '').trim(),
  );

/**
 * Validate that email uses a professional domain (not public email services)
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
export const isValidProfessionalEmail = (email) => {
  if (!isValidEmail(email)) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return Boolean(domain && !PUBLIC_DOMAINS.has(domain));
};

/**
 * Check if an email is already used by another provider in the list
 * @param {string} email - Email to check
 * @param {Array<{email: string}>} providers - List of other providers (excluding current) to check against
 * @returns {boolean}
 */
export const isDuplicateEmail = (email, providers) => {
  if (!email) return false;
  return providers.some(
    (provider) =>
      provider.email?.trim().toLowerCase() === email.trim().toLowerCase(),
  );
};

/**
 * Check if a provider is already in the roster
 * @param {string} providerCode - Provider code to check
 * @param {Array<{providerCode: string}>} providers - List of providers to check against
 * @returns {boolean}
 */
export const isDuplicateProvider = (providerCode, providers) => {
  if (!providerCode) return false;
  return providers.some((provider) => provider.providerCode === providerCode);
};

/**
 * Validate Last 4 format (exactly 4 alphanumeric characters).
 *
 * @example
 * isValidLast4Format('AB12') // true
 * isValidLast4Format('abc')  // false (only 3 chars)
 * isValidLast4Format('AB!2') // false (special char)
 *
 * @param {string} value - The input value
 * @returns {boolean} True if valid format
 */
export const isValidLast4Format = (value = '') =>
  /^[A-Za-z0-9]{4}$/.test((value || '').trim());
